import { IStructuredDataContext } from "@/types/utils/seo/structuredData/types/context";

export type Builder = (input: IStructuredDataContext) => Record<string, any> | undefined;

export const category: Builder = ({ vm, currentUrl, origin }) => {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": ["WebPage", "Article"],
                "@id": `${currentUrl}#webpage`,
                "url": `${currentUrl}`,
                "name": `${vm?.seo?.title}`,
                "inLanguage": vm?.cultureInfo,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "mainEntity": { "@id": `${currentUrl}#article` }
            },
            {
                "@type": "NewsArticle",
                "@id": `${currentUrl}#article`,
                "mainEntityOfPage": {
                    "@id": `${currentUrl}#webpage`
                },
                "headline": vm?.title,
                "description": vm?.body,
                "datePublished": vm?.publishedOn,
                "inLanguage": vm?.cultureInfo,
                "author": {
                    "@type": vm?.author?.function,
                    "name": vm?.author?.name
                },
                "publisher": {
                    "@id": `${origin}/#organization`
                }
            }
        ]
    };
};

export const overviews: Builder = ({ vm, currentUrl, origin }) => {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": ["CollectionPage", "WebPage"],
                "@id": `${currentUrl}`,
                "url": `${currentUrl}`,
                "name": `${vm?.seo?.title}`,
                "inLanguage": vm?.cultureInfo,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "description": `${vm?.seo?.metaDescription}`
            },
        ]
    };
};

export const software: Builder = ({ vm, currentUrl, creator, currentCurrency, formatProductName, origin }) => {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Website",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": currentUrl,
                "url": currentUrl,
                "name": vm?.title,
                "description": vm?.seo?.metaDescription,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "mainEntity": { "@id": `${currentUrl}#product` },
            },
            {
                "@type": "Product",
                "@id": `${origin}#product`,
                "name": formatProductName(vm.name, vm.brand) + " Subscription",
                "description": "Abonnement op " + formatProductName(vm.name, vm.brand) + ", beschikbaar als licentie.",
                "brand": {
                    "@type": "Brand",
                    "name": creator || vm.brand
                },
                "image": vm.iconUrl,
                "url": currentUrl,
                "offers": (Array.isArray(vm.variationOptions) && vm.variationOptions.length > 0) ?
                    vm.variationOptions?.map(variation => ({
                        "@type": "Offer",
                        "url": currentUrl,
                        "price": variation.price ?? "On Request",
                        "priceCurrency": currentCurrency,
                        "description": variation.duration + "-month subscription",
                        availability: "https://schema.org/InStock",
                    })) :
                    [{
                        "@type": "Offer",
                        url: currentUrl,
                        price: 0,
                        priceCurrency: currentCurrency,
                        availability: "https://schema.org/OutOfStock",
                        description: "Unavailable"
                    }],
                "isRelatedTo": { "@id": `${currentUrl}#softwareApplication`, }
            },
            {
                "@type": "SoftwareApplication",
                "@id": `${origin}#softwareApplication`,
                "name": formatProductName(vm?.name, vm?.brand),
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Windows",
                "description": vm?.intro,
                "image": vm?.iconUrl,
                "creator": {
                    "@type": "Organization",
                    "name": vm?.brand
                }
            }
        ]
    }
};

export const training: Builder = ({ vm, origin, currentUrl, creator, currentCurrency, formatProductName }) => {
    if (!vm?.trainingProductGroupId) return;
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Website",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": currentUrl,
                "url": currentUrl,
                "name": vm?.title,
                "description": vm?.seo?.metaDescription,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "mainEntity": { "@id": `${currentUrl}#product` },
            },
            {
                "@type": "Course",
                "@id": `${currentUrl}#course`,
                "name": formatProductName(vm?.name, vm?.brand),
                "description": vm?.intro,
                "provider": {
                    "@type": "Organization",
                    "name": creator,
                    "url": origin
                },
                "isBasedOn": { "@id": `${currentUrl}#product` }
            },
            {
                "@type": "Product",
                "@id": `${currentUrl}#product`,
                "name": formatProductName(vm?.name, vm?.brand),
                "category": "EducationalCourse",
                "brand": {
                    "@type": "Organization",
                    "name": vm?.brand
                },
                "image": vm?.iconUrl,
                "url": currentUrl,
                "description": formatProductName(vm?.name, vm?.brand),
                "offers":
                {
                    "@type": "Offer",
                    "url": currentUrl,
                    "price": vm?.modules?.reduce((sum, m) => sum + (m.price ?? 0), 0) ?? 0,
                    "priceCurrency": currentCurrency,
                    "availability": "https://schema.org/InStock",
                    "businessFunction": "https://schema.org/ProvideService"
                },
                "isRelatedTo": { "@id": `${currentUrl}#course`, }
            }
        ]
    }
};

export const expert: Builder = ({ vm, origin, currentUrl, currentCurrency }) => {
    if (!vm?.expertProduct || !vm?.expertProfile) return;
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Website",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": currentUrl,
                "url": currentUrl,
                "name": vm?.title,
                "description": vm?.seo?.metaDescription,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "provider": { "@id": `${currentUrl}#person` },
                "mainEntity": { "@id": `${currentUrl}#product` },
            },
            {
                "@type": "Person",
                "@id": `${currentUrl}#person`,
                "name": vm?.expertProfile?.firstName + " " + vm?.expertProfile?.lastName,
                "jobTitle": vm?.expertProfile?.function,
                "image": origin + vm?.expertProfile?.imageUrl,
                "url": currentUrl
            },
            {
                "@type": "Product",
                "@id": `${currentUrl}#product`,
                "name": vm?.expertProfile?.displayName,
                "description": vm?.expertProfile?.description,
                "image": origin + vm?.expertProfile?.imageUrl,
                "offers": vm?.expertProfile?.expertLevelProduct?.variations?.map(variation => ({
                    "@type": "Offer",
                    "name": variation.displayName,
                    "url": currentUrl,
                    "priceCurrency": currentCurrency,
                    "price": variation.price,
                })),
                "brand": { "@id": `${origin}/#organization` }
            }
        ]
    }
};

export const service: Builder = ({ vm, origin, currentUrl, creator }) => {
    if (!vm.cadacIdentifier) return;
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": currentUrl,
                "url": currentUrl,
                "name": vm?.title,
                "description": vm?.seo?.metaDescription,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "mainEntity": { "@id": `${currentUrl}#service` }
            },
            {
                "@type": "Service",
                "@id": `${currentUrl}#service`,
                "name": vm?.title,
                "serviceType": "Technical Support",
                "provider": { "@id": currentUrl },
                "brand": {
                    "@type": "Brand",
                    "name": creator
                },
                "description": vm?.seo?.metaDescription,
                "url": currentUrl
            }
        ]
    }
};

export const content: Builder = ({ vm, currentUrl, origin }) => {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${origin}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": `${currentUrl}/#webpage`,
                "url": `${currentUrl}`,
                "name": `${vm?.seo?.title}`,
                "description": `${vm?.seo?.metaDescription}`,
                "inLanguage": `${vm?.currentCulture}`,
                "keywords": vm?.seo?.metaKeywords,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` },
                "mainEntity": { "@id": `${currentUrl}#entity` }
            },
            {
                "@type": "Thing",
                "@id": `${currentUrl}#entity`,
                "name": `${vm?.seo?.title}`,
                "description": `${vm?.seo?.metaDescription}`,
                "url": `${currentUrl}`
            }
        ]
    };
};

export const website: Builder = ({ vm, origin, currentUrl }) => {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${currentUrl}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": currentUrl,
                "url": currentUrl,
                "name": vm?.title,
                "description": vm?.seo?.metaDescription,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` }
            },
        ]
    }
}

export const Website: Builder = ({ vm, origin, currentUrl }) => {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebSite",
                "@id": `${origin}/#website`,
                "url": `${origin}/`,
                "name": "Cadac Group | Enabling digital. | Autodesk Platinum Partner",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${currentUrl}?type=all&query=`
                }
            },
            {
                "@type": "Organization",
                "@id": `${origin}/#organization`,
                "name": "Cadac Group",
                "url": `${origin}`
            },
            {
                "@type": "WebPage",
                "@id": currentUrl,
                "url": currentUrl,
                "name": vm?.title,
                "description": vm?.seo?.metaDescription,
                "isPartOf": { "@id": `${origin}/#website` },
                "publisher": { "@id": `${origin}/#organization` }
            },
        ]
    }
}